/**
 * Pure formatters used across the Order details page. No React, no
 * local types — safe to import anywhere.
 */

export function formatWeight(kg: number): string {
  return `${kg.toLocaleString('en-IE')} kg`;
}

export function formatFileSize(bytes: number | null | undefined): string {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(kb >= 100 ? 0 : 1)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(mb >= 100 ? 0 : 1)} MB`;
}

export function formatMoney(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${amount.toLocaleString('en-IE')} ${currency}`;
  }
}

/** Compose a temperature range hint from the property-group fields. */
export function formatTempSpecRange(
  min: number | null | undefined,
  max: number | null | undefined,
  days: number | null | undefined,
): string {
  const parts: string[] = [];
  if (min != null) parts.push(`Min: ${min}`);
  if (max != null) parts.push(`Max: ${max}`);
  if (days != null) parts.push(`Max Transit Days: ${days}`);
  return parts.length > 0 ? parts.join(', ') : 'Not specified';
}

/**
 * Range-check a temperature against a cargo's min/max spec. Returns an
 * operator-facing violation message, or null when in-range / unbounded /
 * unset. Shared by the order-level box and the per-task boxes so the
 * wording stays identical across all three save paths.
 */
export function temperatureRangeViolation(
  temp: number | null | undefined,
  min: number | null | undefined,
  max: number | null | undefined,
): string | null {
  if (temp == null || min == null || max == null) return null;
  if (temp < min || temp > max) {
    return (
      `Temperature ${temp}°C is outside the allowed range ` +
      `(${min}°C to ${max}°C) for this cargo.`
    );
  }
  return null;
}
